// 9INE theme: the header and its full-width menus, search, the phone menu, the sun-moon switch, hover scrambles,
// Motion on/off, the loading screen and the iris between pages. Ported from the approved concept (concept/src).
import {
  $, $$, root, store, session, editor, motion, setMotion,
  setText, decode, scramble, pixelWipe, pixelateAway, tween, EASE, place, wait, checkFirst
} from 'fx';
import { open, close, isOpen, closeAll } from 'layers';
import { initCart, openCart } from 'cart';
import { initConsent } from 'consent';
import { initGeo } from 'geo';
import { initNotices } from 'notices';

const base = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------- header: menu caret, full-width menus, search, phone menu ---------- */
function initHeader() {
  const nav = $('#nav');
  const caret = nav && $('.caret', nav);
  const dropScrim = $('#drop-scrim');
  const current = () => nav && $('[aria-current="page"], [data-current]', nav);
  const placeCaret = (instant) => { if (nav && !nav.matches(':hover')) place(caret, current(), instant); };

  if (nav) {
    $$('a, button', nav).forEach((a) => a.addEventListener('mouseenter', () => place(caret, a)));
    nav.addEventListener('mouseleave', () => place(caret, current()));
    placeCaret(true);
    window.addEventListener('resize', () => placeCaret(true));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => placeCaret(true));
  }

  // Full-width menus (Shop): one open at a time, closed by Escape, the scrim or a second click.
  $$('.nav-drop').forEach((btn) => {
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    btn.addEventListener('click', () => {
      if (isOpen(panel)) { close(panel); return; }
      closeAll();
      open(panel, {
        scrim: dropScrim,
        focus: btn,
        extra: [btn],
        onClose: () => btn.setAttribute('aria-expanded', 'false')
      });
      btn.setAttribute('aria-expanded', 'true');
      $$('.label', panel).forEach((l, k) => decode(l, { delay: 120 + k * 60, step: 18 }));
    });
  });
  if (dropScrim) dropScrim.addEventListener('click', () => closeAll());

  // Search panel with Shopify's live search.
  const search = $('#search');
  if (search) {
    const input = $('#search-input', search);
    const results = $('#search-results', search);
    const meta = $('#search-meta', search);
    const popular = results.innerHTML;
    const popularText = meta.textContent;
    let timer = 0;
    let ctrl = null;
    const run = async (q) => {
      if (ctrl) ctrl.abort();
      if (!q) { results.innerHTML = popular; setText(meta, popularText); return; }
      ctrl = new AbortController();
      try {
        const url = `${base()}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: ctrl.signal });
        const html = await res.text();
        const found = new DOMParser().parseFromString(html, 'text/html').querySelector('.search-found');
        const n = found ? Number(found.dataset.count) : 0;
        results.innerHTML = found ? found.innerHTML : '';
        const phrase = n === 1 ? search.dataset.foundOne : search.dataset.found;
        setText(meta, n ? (phrase || '{n} results for “{q}”').replace('{n}', n).replace('{q}', q)
          : (search.dataset.none || 'No products match “{q}”').replace('{q}', q));
      } catch (err) { /* a newer search replaced this one */ }
    };
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => run(input.value.trim()), 220); });
    $$('[data-open="search"]').forEach((a) => a.addEventListener('click', (e) => {
      e.preventDefault();
      closeAll();
      open(search, { scrim: dropScrim, focus: input });
      decode(meta, { delay: 100, step: 18 });
    }));
  }

  // Phone menu.
  const menu = $('#menu');
  const menuBtn = $('#menu-btn');
  if (menu && menuBtn) {
    menuBtn.addEventListener('click', () => {
      if (isOpen(menu)) { close(menu); return; }
      closeAll();
      open(menu, {
        focus: $('.menu-link', menu),
        extra: [menuBtn],
        onClose: () => {
          menuBtn.setAttribute('aria-expanded', 'false');
          setText(menuBtn, menuBtn.dataset.label || 'Menu');
          scramble(menuBtn);
        }
      });
      menuBtn.dataset.label = menuBtn.dataset.label || menuBtn.textContent.trim();
      menuBtn.setAttribute('aria-expanded', 'true');
      setText(menuBtn, menuBtn.dataset.closeLabel || 'Close');
      scramble(menuBtn);
      $$('.menu-nav [data-fx]', menu).forEach((x, k) => decode(x, { delay: 160 + k * 80, step: 26, hold: 150 }));
    });
  }

  // Cart.
  $$('[data-open="cart"]').forEach((a) => a.addEventListener('click', (e) => {
    if (!$('#cart')) return;
    e.preventDefault();
    closeAll();
    openCart();
  }));
}

/* ---------- the header gets the page background once the page scrolls, so text never runs under it ---------- */
function initScrolled() {
  let on = null;
  const check = () => {
    const now = window.scrollY > 4;
    if (now !== on) root.classList.toggle('scrolled', (on = now));
  };
  window.addEventListener('scroll', check, { passive: true });
  check();
}

/* ---------- sun-moon switch ---------- */
function initThemeSwitch() {
  const sw = $('#theme-switch');
  if (!sw) return;
  const lightMQ = window.matchMedia('(prefers-color-scheme: light)');
  const currentTheme = () => {
    const t = root.getAttribute('data-theme');
    if (t === 'light' || t === 'dark') return t;
    return lightMQ.matches ? 'light' : 'dark';
  };
  const sync = () => sw.setAttribute('aria-checked', String(currentTheme() === 'dark'));
  sw.addEventListener('click', () => {
    const next = currentTheme() === 'dark' ? 'light' : 'dark';
    store.set('9ine-theme', next);
    const color = getComputedStyle(root).getPropertyValue(next === 'dark' ? '--c-dark-bg' : '--c-light-bg').trim();
    pixelWipe(color || (next === 'dark' ? '#0A0A0A' : '#F7F7F5'), () => { root.setAttribute('data-theme', next); sync(); });
  });
  new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['data-theme'] });
  if (lightMQ.addEventListener) lightMQ.addEventListener('change', sync);
  sync();
}

/* ---------- hover scramble ---------- */
const HOVER = '.nav a, .nav-drop, .tool, .socials a, .policies a, .linkish, .go, .close, .btn, .add, .mega-list a, .menu-sub a, .psocial-list a, .tabs button, .partner, .news-row, .info a';
document.addEventListener('mouseover', (e) => {
  const t = e.target.closest(HOVER);
  if (!t || (e.relatedTarget && t.contains(e.relatedTarget))) return;
  let target = t.querySelector('[data-hover]');
  if (!target) target = t.matches('.add') ? t.firstElementChild : t;
  if (target && !target.querySelector('svg, img')) scramble(target);
});

/* ---------- Motion on/off (the start page has the switch; any [data-motion-toggle] works) ---------- */
function syncMotion() {
  $$('[data-motion-toggle]').forEach((b) => {
    b.setAttribute('aria-checked', String(motion));
    // A text switch shows its state in words; the icon switch (footer) shows it with its knob.
    const label = $('[data-hover]', b);
    if (label && b.dataset.on) setText(label, motion ? b.dataset.on : (b.dataset.off || b.dataset.on));
  });
  $$('[data-motion-video]').map((el) => (el.matches('video') ? el : $('video', el))).filter(Boolean).forEach((v) => {
    if (motion && !saveData()) v.play().catch(() => { /* autoplay refused */ }); else v.pause();
  });
}
// A browser saving data (its data-saver setting, or a 2G connection) keeps the background videos' still image.
function saveData() {
  const c = navigator.connection;
  return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
}
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-motion-toggle]')) { setMotion(!motion); syncMotion(); }
});

/* ---------- the page's own entrance: blocks rise, marked text decodes ---------- */
function enter() {
  root.classList.add('page-in');
  document.dispatchEvent(new CustomEvent('page:in'));
  let k = 0;
  $$('#main [data-fx]').forEach((fx) => {
    decode(fx, fx.matches('.line') ? { delay: 120 + k++ * 260, step: 30, hold: 170 } : { delay: 380 + k++ * 110, step: 20, hold: 130 });
  });
}

/* ---------- loading screen (first page of a visit) ---------- */
function runLoader() {
  const loader = $('#loader');
  if (!root.classList.contains('is-loading') || !loader) return Promise.resolve();
  const ticksEl = $('#ruler-ticks');
  const head = $('#ruler-head');
  const pct = $('#loader-pct');
  const TOTAL = 41 * 7;
  const ticks = [];
  for (let i = 0; i < TOTAL; i++) {
    const t = document.createElement('i');
    if (i % 7 === 3) t.className = 'major';
    ticksEl.append(t);
    ticks.push(t);
  }
  let ready = document.readyState === 'complete';
  window.addEventListener('load', () => { ready = true; }, { once: true });
  let gone = false;
  const leave = (res) => {
    if (gone) return;
    gone = true;
    session.set('9ine-seen', '1');
    loader.classList.add('is-leaving');
    res();
    // The crest breaks up into pixels (about 0.75 s) while the background fades (0.2 s delay + 0.9 s), then the
    // loader goes. The timer is a backstop in case the browser pauses the effect.
    Promise.all([pixelateAway($('.loader-logo', loader)), wait(motion ? 1100 : 0)])
      .then(() => root.classList.remove('is-loading'));
    setTimeout(() => root.classList.remove('is-loading'), motion ? 1600 : 0);
  };
  return new Promise((res) => {
    const t0 = performance.now();
    setTimeout(() => leave(res), 7000); // hard stop, even if the browser pauses animation frames
    if (!motion) {
      const check = () => (ready || performance.now() - t0 > 2500 ? leave(res) : setTimeout(check, 80));
      check();
      return;
    }
    let x = 0;
    let filled = -1;
    let skip = false;
    loader.addEventListener('click', () => { skip = true; }, { once: true });
    const frame = () => {
      const elapsed = performance.now() - t0;
      const target = skip || elapsed > 5000 ? 100 : Math.min((elapsed / 1700) * 100, ready ? 100 : 90);
      x += (target - x) * (skip ? 0.35 : 0.1);
      if (target >= 100 && x > 99.6) x = 100;
      pct.textContent = `${String(Math.floor(x)).padStart(2, '0')}%`;
      head.style.transform = `translateX(${(6 + (x / 100) * ticksEl.clientWidth - 7).toFixed(1)}px)`;
      const upto = Math.floor((x / 100) * (TOTAL - 1));
      if (upto !== filled) {
        for (let i = 0; i < TOTAL; i++) ticks[i].classList.toggle('is-filled', i <= upto);
        filled = upto;
      }
      if (x >= 100) { setTimeout(() => leave(res), 160); return; }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  });
}

/* ---------- iris between pages (Remy) ---------- */
// Clicking a link to another page of the store closes the iris to the background colour, then the next page starts
// covered (the boot script adds iris-shut) and opens it. Other sites, new tabs and same-page links are left alone.
const iris = $('#iris');
const irisOn = () => !!iris && motion && !editor && document.body.dataset.iris === 'true';
let irisRGB = '10,10,10';
function paintIris(r) {
  iris.style.background = `radial-gradient(circle at 50% 50%, rgba(${irisRGB},0) ${Math.max(0, r - 45)}%, rgba(${irisRGB},.7) ${Math.max(0, r - 18)}%, rgb(${irisRGB}) ${r}%)`;
}
function readIrisColor() {
  const c = document.createElement('canvas').getContext('2d');
  c.fillStyle = getComputedStyle(root).getPropertyValue('--bg').trim() || '#0A0A0A';
  const hex = c.fillStyle;
  if (/^#[0-9a-f]{6}$/i.test(hex)) irisRGB = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(',');
}
function internalLink(a, e) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  if ((a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('data-no-iris')) return null;
  const url = new URL(a.href, location.href);
  if (url.origin !== location.origin) return null;
  if (url.pathname === location.pathname && url.search === location.search) return null;
  return url;
}
document.addEventListener('click', async (e) => {
  const a = e.target.closest('a[href]');
  if (!a || !irisOn()) return;
  const url = internalLink(a, e);
  if (!url) return;
  e.preventDefault();
  closeAll();
  readIrisColor();
  session.set('9ine-iris', '1');
  iris.classList.add('on');
  paintIris(165);
  await Promise.race([tween(600, (t) => paintIris(165 * (1 - t)), EASE.inQuad), wait(900)]);
  location.href = url.href;
});
// Fetch a page as soon as the pointer rests on its link, so it is ready when the iris has closed.
const prefetched = new Set();
document.addEventListener('pointerover', (e) => {
  const a = e.target.closest('a[href]');
  if (!a || !irisOn()) return;
  const url = new URL(a.href, location.href);
  if (url.origin !== location.origin || prefetched.has(url.href) || url.pathname.startsWith('/cart')) return;
  prefetched.add(url.href);
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url.href;
  document.head.append(link);
});
async function openIris() {
  if (!root.classList.contains('iris-shut')) return;
  session.remove('9ine-iris');
  if (!iris) { root.classList.remove('iris-shut'); return; }
  readIrisColor();
  iris.classList.add('on');
  paintIris(0);
  root.classList.remove('iris-shut');
  await Promise.race([tween(800, (t) => paintIris(165 * t), EASE.outQuad), wait(1200)]);
  iris.classList.remove('on');
  iris.style.background = '';
}
// Coming back with the browser's back button can restore this page with the iris still closed.
window.addEventListener('pageshow', (e) => {
  if (!e.persisted || !iris) return;
  iris.classList.remove('on');
  iris.style.background = '';
  root.classList.remove('iris-shut');
  session.remove('9ine-iris');
});

/* ---------- the gift card in the Shop panel: turns towards the mouse while the light streak follows it ----------
   By itself it rocks (theme.css, gift-rock); under the mouse it tilts to the pointer, and when the mouse leaves it
   eases back to where the rock starts, then rocks again. With Motion off it stands still. */
function initGiftTilt() {
  const card = $('.mega-gift-card');
  if (!card) return;
  const area = card.closest('.mega-gift');
  let back = 0;
  area.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || !motion) return;
    clearTimeout(back);
    const r = card.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    card.classList.remove('is-back');
    card.classList.add('is-live');
    card.style.setProperty('--ry', `${((x - 0.5) * 30).toFixed(1)}deg`);
    card.style.setProperty('--rx', `${((0.5 - y) * 18).toFixed(1)}deg`);
    card.style.setProperty('--sx', `${(115 - x * 130).toFixed(1)}%`);
  });
  area.addEventListener('pointerleave', () => {
    if (!card.classList.contains('is-live')) return;
    card.classList.replace('is-live', 'is-back');
    ['--ry', '--rx', '--sx'].forEach((p) => card.style.removeProperty(p));
    back = setTimeout(() => card.classList.remove('is-back'), 450);
  });
}

/* ---------- email sign-ups (footer, "Sold out in your size?"): checked before sending, in our words ----------
   The browser's own bubbles ("Please include an '@'…") are switched off; the message decodes in under the form
   (snippets/email-error) and goes away as soon as the email is fixed. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function initEmailForms() {
  $$('form.signup, form.p-notify-form').forEach((form) => {
    const input = $('input[type="email"]', form);
    const err = $('[data-email-err]', form);
    if (!input || !err) return;
    form.noValidate = true;
    const check = () => {
      const v = input.value.trim();
      const msg = !v ? err.dataset.empty : EMAIL_RE.test(v) ? '' : err.dataset.format;
      input.setAttribute('aria-invalid', String(!!msg));
      err.hidden = !msg;
      if (msg && err._msg !== msg) {
        setText(err, msg);
        decode(err, { step: 12, hold: 90 });
      }
      err._msg = msg;
      if (msg) input.focus();
      return !msg;
    };
    input.addEventListener('input', () => { if (input.getAttribute('aria-invalid') === 'true') check(); });
    checkFirst(form, check);
  });
}

/* ---------- start ---------- */
initHeader();
initGiftTilt();
initEmailForms();
initScrolled();
initThemeSwitch();
initCart();
initConsent();
initGeo();
initNotices();
syncMotion();

if (editor) {
  enter();
  document.addEventListener('shopify:section:load', (e) => { if (e.target.querySelector('#chrome')) initHeader(); });
} else {
  Promise.all([runLoader(), openIris()]).then(enter);
}
